import { useState } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import LoadingWheel from "../../shared/LoadingWheel";
import { TextInput } from "../../shared/Inputs/TextInput";
import { IShippingAddress } from "./../../../models/Shipping";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import usePartMutations from "../../../data/usePartMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";

interface EditAddressProps {
  address: IShippingAddress;
  partId: string;
  onEditSuccess: (address: IShippingAddress) => void;
}

export function EditAddress({
  address,
  partId,
  onEditSuccess,
}: EditAddressProps) {
  const { t } = useTranslation("stock");
  const { patchPart } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers(t("Shipping"));
  const { register, getValues, handleSubmit } = useForm<IShippingAddress>({
    defaultValues: address,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  const updatePartAddress = (data: IShippingAddress) => {
    setError(undefined);

    if (!data.addressLine1 || data.addressLine1!.length < 4) {
      setError(t("Please fill in Address line 1. Must be at least 4 characters."));
      return;
    }

    if (!data.postcode || data.postcode!.length < 5) {
      setError(t("Please fill in postcode. Must be at least 5 characters."));
      return;
    }

    setSubmitting(true);

    patchPart.mutate(
      {
        ...data,
        id: partId,
      },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          onEditSuccess(data);
        },
        onError: () => {
          saveHandlers.onError();
        },
        onSettled: () => {
          setSubmitting(false);
        },
      }
    );
  };

  return submitting ? (
    <div>
      <LoadingWheel />
    </div>
  ) : (
    <form
      className="grid grid-cols-2 gap-2"
      onSubmit={handleSubmit(updatePartAddress)}
    >
      <TextInput
        label={t("Full Name")}
        register={register}
        registerName={"fullName"}
      />
      <TextInput
        label={`${t("Address Line 1")} (${getValues("addressLine1")?.length}/35) `}
        register={register}
        registerName={"addressLine1"}
      />
      <TextInput
        label={`${t("Address Line 2")} (${getValues("addressLine2")?.length}/35) `}
        register={register}
        registerName={"addressLine2"}
      />
      <TextInput label={t("City")} register={register} registerName={"city"} />
      <TextInput label={t("County")} register={register} registerName={"county"} />
      <TextInput
        label={t("State/Province")}
        register={register}
        registerName={"stateOrProvince"}
      />
      <TextInput
        label={t("Postcode")}
        register={register}
        registerName={"postcode"}
      />
      <div /> {/* spacer */}
      {error && (
        <div className="col-span-2 bg-red-100 text-red-600 px-4 py-2 rounded">
          {error}
        </div>
      )}
      <ButtonInput
        label={t("Save Address Info")}
        isSubmit={false}
        onClick={() => updatePartAddress(getValues())}
        classes={"mt-4"}
      />
    </form>
  );
}
